"use client"

import { t } from "@/languages/i18n"
import { useDomainStore } from "@/stores/use-domain-store"
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  FolderOutlined,
  GlobalOutlined,
  LockOutlined,
  PlusOutlined,
} from "@ant-design/icons"
import { Button, Dropdown, Input, MenuProps, Modal, Popconfirm, Popover, Space, Spin, Tooltip } from "antd"
import { useState } from "react"
import { FolderSelectorProps, MediaFolder } from "../../../../lib/config/types"
import { useFolderSearch } from "../hook/useFolderSearch"
import { useFolderSelector } from "../hook/useFolderSelector"
import { resolveMediaDomainParam } from "../utils/media-domain"

const FOLDER_THEME = "#3B82F6"
const FOLDER_ICON_BG = "rgba(59, 130, 246, 0.12)"

const siteColorFor = (domain: string | undefined): string => {
  if (!domain) return "var(--admin-muted-fg)"
  if (domain.includes("tamdagroup")) return "var(--site-tamdagroup)"
  if (domain.includes("tamdafoods") || domain.includes("tamdaoc")) return "var(--site-tamdafoods)"
  if (domain.includes("tamdaone")) return "var(--site-tamdaone)"
  if (domain.includes("tamdamedia")) return "var(--site-tamdamedia)"
  return "var(--admin-muted-fg)"
}

function folderMetaText(childCount: number, filesCount: number): string {
  return `${childCount} ${t("thư mục")}, ${filesCount} ${t("tệp")}`
}

export default function FolderSelector({
  currentFolderId,
  onFolderChange,
  refreshTrigger,
  onAddAssets,
  compact = false,
  searchTerm,
  onOpenSearchResult,
}: FolderSelectorProps) {
  const vm = useFolderSelector({ currentFolderId, onFolderChange, refreshTrigger })
  const selectedDomain = useDomainStore(state => state.selectedDomain)
  const search = useFolderSearch(searchTerm, resolveMediaDomainParam(selectedDomain))
  // Đang search → lưới thư mục hiện folder khớp tên (toàn thư viện) thay cho
  // folder con của thư mục hiện tại. Mở 1 kết quả thì để Media xoá từ khoá.
  const folderList = search.active ? search.folders : vm.currentChildList
  const listLoading = search.active ? search.loading : vm.loading
  const openItem = (id: number, name: string) =>
    search.active && onOpenSearchResult ? onOpenSearchResult(id) : vm.openFolder(id, name)
  const [popoverOpen, setPopoverOpen] = useState(false)

  const realDomains = vm.domains.filter(d => d.id !== 0)

  const siteMenuItems: MenuProps["items"] = realDomains.map(d => ({
    key: d.attributes?.domain || String(d.id),
    label: (
      <div className="flex items-center gap-2 py-0.5" style={{ minWidth: 180 }}>
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: siteColorFor(d.attributes?.domain),
          }}
        />
        <div className="flex flex-col">
          <span style={{ fontSize: 13, fontWeight: 500 }}>{d.attributes?.name}</span>
          <span style={{ fontSize: 11, color: "var(--admin-muted-fg)" }}>{d.attributes?.domain}</span>
        </div>
      </div>
    ),
    onClick: () => vm.setSelectedDomainForFolder(d.attributes?.domain),
  }))

  const currentSelectedDomainData = vm.domains.find(d => d.attributes?.domain === vm.selectedDomainForFolder)

  const sharedModals = (
    <>
      <Modal
        title="Tạo thư mục mới"
        open={vm.createModalOpen}
        onOk={vm.handleCreateFolder}
        onCancel={vm.closeCreateModal}
        confirmLoading={vm.creating}
        okText="Tạo"
        cancelText="Hủy"
      >
        <div className="py-2">
          <label className="block text-sm font-medium text-[#32324d] mb-2">Tên thư mục</label>
          <Input
            value={vm.newFolderName}
            onChange={e => vm.setNewFolderName(e.target.value)}
            placeholder="Nhập tên"
            onPressEnter={vm.handleCreateFolder}
            autoFocus
          />
        </div>
      </Modal>

      <Modal
        title={`Đổi tên "${vm.renameFolderName}"`}
        open={vm.renameModalOpen}
        onOk={vm.handleRename}
        onCancel={vm.closeRename}
        confirmLoading={vm.renaming}
        okText="Lưu"
        cancelText="Hủy"
      >
        <div className="py-2 flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-[#32324d] mb-2">Tên thư mục</label>
            <Input
              value={vm.renameValue}
              onChange={e => vm.setRenameValue(e.target.value)}
              onPressEnter={vm.handleRename}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#32324d] mb-2">Tên miền</label>
            <Dropdown menu={{ items: siteMenuItems }} trigger={["click"]} placement="bottomLeft">
              <Button className="w-full text-left flex items-center justify-between h-10">
                <Space>
                  <GlobalOutlined style={{ color: siteColorFor(vm.selectedDomainForFolder) }} />
                  <span className="font-medium text-sm">
                    {currentSelectedDomainData?.attributes?.name || vm.selectedDomainForFolder || "Chọn website"}
                  </span>
                </Space>
                <DownOutlined style={{ fontSize: 10 }} />
              </Button>
            </Dropdown>
          </div>
        </div>
      </Modal>
    </>
  )

  // Compact mode: folder picker in a Popover
  if (compact) {
    const breadcrumbLabel = vm.breadcrumb.map(b => b.name).join(" › ")

    const popoverContent = (
      <div style={{ width: 420 }}>
        {!search.active && vm.breadcrumb.length > 1 && (
          <div className="flex items-center gap-1 mb-2 pb-2 border-b border-[#eaeaef]">
            <Button
              size="small"
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={vm.goBack}
              className="text-[#3B82F6]"
            />
            <nav className="flex flex-wrap items-center gap-1 text-xs text-[#3B82F6]">
              {vm.breadcrumb.map((item, index) => (
                <span key={`${item.id ?? "root"}-${index}`} className="flex items-center gap-1">
                  {index > 0 && <span className="text-gray-300">/</span>}
                  <button
                    type="button"
                    className={`hover:underline rounded px-1 ${index === vm.breadcrumb.length - 1 ? "font-semibold text-[#32324d]" : ""}`}
                    onClick={() => vm.goBreadcrumb(index)}
                  >
                    {item.name}
                  </button>
                </span>
              ))}
            </nav>
          </div>
        )}

        <Spin spinning={listLoading}>
          {!vm.canGetFolderTree ? (
            <div className="flex items-center gap-2 py-4 text-gray-400 justify-center">
              <LockOutlined />
              <span className="text-xs">Không có quyền xem thư mục</span>
            </div>
          ) : (
            <div
              className="grid gap-2"
              style={{ gridTemplateColumns: "repeat(2, 1fr)", maxHeight: 240, overflowY: "auto" }}
            >
              {folderList.map(folder => {
                const childCount = (folder as MediaFolder).childrenCount ?? 0
                const filesCount = (folder as MediaFolder).filesCount ?? 0
                return (
                  <div
                    key={folder.id}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        openItem(folder.id, folder.name)
                      }
                    }}
                    onClick={() => openItem(folder.id, folder.name)}
                    className="flex items-center gap-2 p-2 rounded border border-[#eaeaef] hover:border-[#3B82F6] cursor-pointer bg-white transition-colors"
                  >
                    <div
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded"
                      style={{ backgroundColor: FOLDER_ICON_BG }}
                    >
                      <FolderOutlined style={{ color: FOLDER_THEME, fontSize: 14 }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-[#32324d] truncate">{folder.name}</div>
                      <div className="text-[10px] text-[#3B82F6]">{folderMetaText(childCount, filesCount)}</div>
                    </div>
                  </div>
                )
              })}
              {!listLoading && folderList.length === 0 && (
                <p className="text-xs text-gray-400 col-span-2 text-center py-4 m-0">{search.active ? "Không có thư mục khớp" : "Không có thư mục con"}</p>
              )}
            </div>
          )}
        </Spin>

        {vm.canMoveFiles && (
          <div className="mt-2 pt-2 border-t border-[#eaeaef]">
            <Button
              size="small"
              icon={<PlusOutlined />}
              style={{ color: FOLDER_THEME, borderColor: FOLDER_THEME }}
              onClick={() => {
                setPopoverOpen(false)
                vm.openCreateModal()
              }}
            >
              {t("Thêm thư mục mới")}
            </Button>
          </div>
        )}
      </div>
    )

    return (
      <>
        <Popover
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
          trigger="click"
          placement="bottomLeft"
          content={popoverContent}
          arrow={false}
          overlayClassName="!z-[999999]"
        >
          <Button
            size="small"
            icon={<FolderOutlined style={{ color: FOLDER_THEME }} />}
            style={{ maxWidth: 260 }}
          >
            <span
              style={{
                maxWidth: 180,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "inline-block",
                verticalAlign: "middle",
              }}
            >
              {breadcrumbLabel}
            </span>
            <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
          </Button>
        </Popover>
        {sharedModals}
      </>
    )
  }

  // Full mode (non-modal / page view)
  return (
    <div className="rounded-lg border border-[#dcdce4] bg-[#f6f6f9] p-3">
      <div className="flex flex-wrap items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {!search.active && vm.breadcrumb.length > 1 && (
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={vm.goBack}
              className="text-[#3B82F6] hover:text-[#2563EB]"
            />
          )}
          <h3 className="text-base font-semibold text-[#32324d] m-0">
            {search.active
              ? `${t("Thư mục khớp")} "${searchTerm}" (${search.loading ? "…" : folderList.length})`
              : `${vm.activeFolder?.name ?? "Thư mục"} (${vm.currentChildList.length})`}
          </h3>
        </div>

        <div className="flex flex-wrap gap-2">
          {vm.canMoveFiles ? (
            <Button
              icon={<PlusOutlined />}
              onClick={vm.openCreateModal}
              style={{ borderColor: FOLDER_THEME, color: FOLDER_THEME }}
            >
              {t("Thêm thư mục mới")}
            </Button>
          ) : (
            <Tooltip title="Bạn không có quyền tạo thư mục">
              <Button icon={<LockOutlined />} disabled>
                {t("Thêm thư mục mới")}
              </Button>
            </Tooltip>
          )}

          {onAddAssets &&
            (vm.canUploadToFolder ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={onAddAssets} style={{ background: FOLDER_THEME }}>
                {t("Thêm hình ảnh")}
              </Button>
            ) : (
              <Tooltip title="Bạn không có quyền tải ảnh lên">
                <Button type="primary" icon={<LockOutlined />} disabled>
                  {t("Thêm hình ảnh")}
                </Button>
              </Tooltip>
            ))}
        </div>
      </div>

      {!search.active && vm.breadcrumb.length > 1 && (
        <nav className="flex flex-wrap items-center gap-1 text-sm text-[#3B82F6] mb-4">
          {vm.breadcrumb.map((item, index) => (
            <span key={`${item.id ?? "root"}-${index}`} className="flex items-center gap-1">
              {index > 0 && <span className="text-[#c0c0cf]">/</span>}
              <button
                type="button"
                className={`inline-flex items-center gap-1 rounded px-1 py-0.5 hover:bg-white/80 ${index === vm.breadcrumb.length - 1 ? "font-semibold text-[#32324d]" : ""
                  }`}
                onClick={() => vm.goBreadcrumb(index)}
              >
                {item.name}
              </button>
            </span>
          ))}
        </nav>
      )}

      <Spin spinning={listLoading}>
        {!vm.canGetFolderTree ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-[#c0c0cf]">
            <LockOutlined className="text-3xl" />
            <p className="text-sm m-0">Bạn không có quyền xem danh sách thư mục.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {folderList.map(folder => {
                const childCount = (folder as MediaFolder).childrenCount ?? 0
                const filesCount = (folder as MediaFolder).filesCount ?? 0
                return (
                  <div
                    key={folder.id}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        openItem(folder.id, folder.name)
                      }
                    }}
                    onClick={() => openItem(folder.id, folder.name)}
                    className={[
                      "group relative flex items-stretch gap-3 rounded border bg-white p-3 text-left shadow-sm transition-colors cursor-pointer",
                      vm.canMoveFiles && !search.active ? "pr-10" : "pr-3",
                      "border-[#eaeaef] hover:border-[#c0c0cf]",
                      vm.selectedIds.includes(folder.id) ? "ring-2 ring-[#3B82F6] ring-offset-1 border-[#3B82F6]" : "",
                    ].join(" ")}
                  >
                    <div
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md"
                      style={{ backgroundColor: FOLDER_ICON_BG }}
                    >
                      <FolderOutlined className="text-xl" style={{ color: FOLDER_THEME }} />
                    </div>

                    <div className="min-w-0 flex-1 py-0.5">
                      <div className="font-semibold text-[#32324d] truncate leading-tight">{folder.name}</div>
                      <div className="text-xs text-[#3B82F6] mt-1 leading-snug">
                        {folderMetaText(childCount, filesCount)}
                      </div>
                      {/* Kết quả search lấy từ toàn thư viện → ghi folder cha để phân biệt trùng tên */}
                      {search.active && folder.parent?.name && (
                        <div className="text-xs text-[#8e8ea9] mt-0.5 truncate">
                          {t("trong")} {folder.parent.name}
                        </div>
                      )}
                    </div>

                    {vm.canMoveFiles && !search.active && (
                      <div
                        className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={e => e.stopPropagation()}
                      >
                        <Tooltip title="Đổi tên">
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            className="text-[#3B82F6]"
                            onClick={() => vm.openRename(folder.id, folder.name)}
                          />
                        </Tooltip>
                        <Popconfirm
                          title="Xóa thư mục?"
                          description={`Bạn có chắc muốn xóa "${folder.name}"?`}
                          onConfirm={() => vm.handleDelete(folder.id)}
                          okText="Xóa"
                          cancelText="Hủy"
                          okButtonProps={{ danger: true, loading: vm.deleting }}
                        >
                          <Tooltip title="Xóa">
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                          </Tooltip>
                        </Popconfirm>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {!listLoading && folderList.length === 0 && (
              <p className="text-sm text-[#3B82F6] text-center py-8 m-0">{search.active ? "Không có thư mục nào khớp từ khoá." : "Chưa có thư mục ở đây."}</p>
            )}
          </>
        )}
      </Spin>

      {sharedModals}
    </div>
  )
}
